import axios from '../../common/utils/interceptor';
import localizeContentMapper from '../../common/utils/localizeContentMapper';
import React, { useEffect, useRef, useState } from 'react';
import taskItemStatusMapper from '../utils/taskItemStatusMapper';
import TaskListLayout01 from './layout_01/TaskListLayout';
import TaskListLayout02 from './layout_02/TaskListLayout';
import useHotjar from '../../hotjar/useHotjar';
import { apiUrls, SessionStore, WorkflowItemCode } from '../../constants';
import { FullPageLoader } from '../../common/components';
import { GAPageViewMappings, GARouteMappings } from '../../constants/gaMappings';
import { getAssessmentSupportedOnDesktop } from '../utils/getAssessmentSupportedOnDesktop';
import { getNextLinearTask } from '../utils/getNextLinearTask';
import { getTemplateLayout } from '../../common/utils/fallbackCondition';
import { initializeTask } from '../utils/initializeTask';
import { sendPageViewDetails } from '../../common/utils/gaEvents';
import { Task, TaskListConfiguration, TaskListLayoutProps } from '../interfaces';
import { TaskItemStatus, taskListDefaultConfiguration } from '../constants';
import { useEndWorkflow, usePollPendingAssessmentItem } from '../hooks';
import { useNavigate, useLocation } from 'react-router-dom';
import { getCookie } from '../../common/utils/cookies';
import { useMediaQuery, useTheme } from '@mui/material';
import { useAppSelector } from '../../store/hook';
import { checkIfRRAppliedButNotFullRR } from './../utils/checkifRRAppliedButNotFullRR';
import { useResponseReuse } from './../hooks/useResponseReuse';
import PromptBox from './../../common/components/PromptBox';
import { useTranslation } from 'react-i18next';
import getAdvancedResponseReuseInfo from '../utils/getAdvancedResposneReuseInfo';
import RRMessagePopup from './../../basicTaskList/components/RRMessagePopup';
import { timeLineProvider } from '../../store/providers/timeLineProvider';

let timeoutId: NodeJS.Timeout;
const TaskListLayoutComponentsMap: { [key: string]: React.FC<TaskListLayoutProps> } = {
  TEMPLATE_LAYOUT_01: TaskListLayout01,
  TEMPLATE_LAYOUT_02: TaskListLayout02,
  TEMPLATE_LAYOUT_04: TaskListLayout02,
};
/**
 * TaskListPage
 */
const TaskListPage = (): React.JSX.Element => {
  /** Page View GA method */
  useEffect(() => {
    sendPageViewDetails(GARouteMappings.TASKLIST, GAPageViewMappings.TASKLIST);
  }, []);

  const tcPlusFlow = getCookie('TcPlusFlag') === '1';

  const template = useAppSelector((state) => state.template);
  const { isICPEnabled } = useAppSelector((state) => state.headerDetails);
  const { setReverseAnimation } = timeLineProvider();
  const { reverseAnimation } = useAppSelector((state) => state.timeLine);
  const candidate = useAppSelector((state) => state.candidate);
  const currentWorkflowItem = useAppSelector((state) => state.currentWorkflowItem);
  const { userSessionActive } = useAppSelector((state) => state.basicDetails);
  const { code, configuration, localizedContentMap, nestedItemsState } = currentWorkflowItem;
  const [isUseEndWorkflowCompleted, setIsUseEndWorkflowCompleted] = useState(false);

  const parsedConfig =
    configuration && code === WorkflowItemCode.TASKLIST
      ? JSON.parse(configuration).configWidgetJSON
      : taskListDefaultConfiguration;
  const taskListConfiguration: TaskListConfiguration = localizeContentMapper(parsedConfig, localizedContentMap);

  // for tc+ flow it will be true by default and for TC it will be false
  const [showTileBanner, setShowTileBanner] = useState(tcPlusFlow);
  const { t } = useTranslation();

  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const smDown = useMediaQuery(theme.breakpoints.down('sm'));
  const [desktopAssessments, setDesktopAssessments] = useState<boolean>(false);
  const { surveyEnabled, surveyTriggerCode, linearflow, showTaskList } = taskListConfiguration;
  const tasklistRef = useRef<HTMLDivElement>(null);

  const tasks = taskItemStatusMapper(taskListConfiguration.tasks, nestedItemsState);
  const stringifiedTasks = JSON.stringify(tasks);
  const isAssessmentPresent = tasks.some((task) => task.containAssessmentItem);

  const rrAppliedButNotFullRR = checkIfRRAppliedButNotFullRR(tasks);
  const isPartialRRMsgAcknowledged = sessionStorage.getItem(SessionStore.partialRRMsgRead) === 'true';
  const [showPartialRRMessage, setShowPartialRRMessage] = useState(
    rrAppliedButNotFullRR && !isPartialRRMsgAcknowledged,
  );
  // Set showPartialRRMessage true on rrAppliedButNotFullRR response update
  useEffect(() => {
    if (rrAppliedButNotFullRR && !isPartialRRMsgAcknowledged) setShowPartialRRMessage(true);
    else setShowPartialRRMessage(false);
  }, [rrAppliedButNotFullRR, isPartialRRMsgAcknowledged]);

  if (showPartialRRMessage) sessionStorage.setItem(SessionStore.partialRRMsgRead, 'false');

  // Notify user if RR is applied on any of the tasks
  useResponseReuse(tasks);

  const handlePartialRRMsg = (): void => {
    sessionStorage.setItem(SessionStore.partialRRMsgRead, 'true');
    setShowPartialRRMessage(false);

    // Make tasklist interactive/accessible/focusable when RR popup is removed
    tasklistRef.current?.removeAttribute('inert');
  };

  const { isRRApplied, rrPopupInfo, frrAppliedOnAllStages } = getAdvancedResponseReuseInfo(tasks);

  const rrMsgRead = sessionStorage.getItem(SessionStore.basicRRMsgRead) !== 'true';
  const [showRRPopup, setShowRRPopup] = useState<boolean>();

  const handleAdvancedRRBtnClick = (showPopup: boolean): void => {
    if (rrMsgRead) sessionStorage.setItem(SessionStore.basicRRMsgRead, 'true');

    setShowRRPopup(showPopup);

    // Make tasklist interactive/non-interactive when RR popup is shown/hidden
    if (showPopup) {
      tasklistRef.current?.setAttribute('inert', '');
    } else {
      tasklistRef.current?.removeAttribute('inert');
    }
  };

  useEffect(() => {
    setShowRRPopup(isRRApplied && rrMsgRead);
  }, [isRRApplied, rrMsgRead]);

  if (reverseAnimation) setReverseAnimation(false);

  const getSchedulableAssessments = async (): Promise<void> => {
    try {
      const { data } = await axios.get(apiUrls.GET_ASSESSMENTS);
      sessionStorage.setItem(SessionStore.schedulableAssessments, JSON.stringify(data));
    } catch (e) {
      console.log(e);
    } finally {
      setShowTileBanner(true);
    }
  };

  useEffect(() => {
    if (!tcPlusFlow) getSchedulableAssessments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (smDown && isAssessmentPresent) {
      const getDesktopBoolValue = async (): Promise<void> => {
        try {
          const response = await getAssessmentSupportedOnDesktop();
          if (response) {
            setDesktopAssessments(response);
          }
        } catch (e) {
          console.error(`Error in fetching the desktop assessment details`, e);
        }
      };

      getDesktopBoolValue();
    }
  }, [smDown, isAssessmentPresent]);

  const [linearTask, setLinearTask] = useState<Task | undefined>();
  const [isTaskInitialized, setIsTaskInitialized] = useState<boolean>(false);
  useEffect(() => {
    if (linearflow && !showTaskList && !linearTask) {
      const allowRedirect = sessionStorage.getItem(SessionStore.allowRedirectToTasklist) === 'true';
      if (!allowRedirect) {
        setLinearTask(getNextLinearTask(tasks));
      }
    }
  }, [tasks, linearflow, showTaskList, linearTask]);

  useEffect(() => {
    clearTimeout(timeoutId);

    let isAnyTaskHurdleFailed = tasks.some((task: Task) => task.status === TaskItemStatus.HURDLE_FAILED);
    if (tcPlusFlow) isAnyTaskHurdleFailed = tasks.some((i: Task) => i.hurdleStatus === TaskItemStatus.HURDLE_FAILED);

    if (linearTask && !isAnyTaskHurdleFailed) {
      timeoutId = setTimeout(async () => {
        await initializeTask(
          linearTask,
          userSessionActive,
          navigate,
          location,
          candidate,
          null,
          undefined,
          currentWorkflowItem,
        );
        setIsTaskInitialized(true);
      }, 200);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linearTask, history, location, userSessionActive, stringifiedTasks, candidate, currentWorkflowItem, tcPlusFlow]);

  /** Redirect to EndPage or Hurdle Page if Workflow completed */
  const verifyingWorkflowCompletion = useEndWorkflow(tasks, setIsUseEndWorkflowCompleted);
  useHotjar(surveyEnabled, surveyTriggerCode, isUseEndWorkflowCompleted);

  /** Polling for Evaluation pending Task item */
  const isPollingAssessmentStatus = usePollPendingAssessmentItem(tasks);

  const layout = getTemplateLayout(template.layoutCode);
  /** show rr popup whlie response reuse applied  */
  const showRRMessagePopup = showPartialRRMessage || (tcPlusFlow && (showPartialRRMessage || frrAppliedOnAllStages));

  // Making tasklist inert when RR popup is shown or loading
  useEffect(() => {
    if ((showRRMessagePopup && showRRPopup) || verifyingWorkflowCompletion || isPollingAssessmentStatus) {
      tasklistRef.current?.setAttribute('inert', '');
    } else {
      tasklistRef.current?.removeAttribute('inert');
    }
  }, [showRRMessagePopup, showRRPopup, verifyingWorkflowCompletion, isPollingAssessmentStatus]);

  if (linearTask && !isTaskInitialized) return <FullPageLoader isLoading />;

  return (
    <>
      {(verifyingWorkflowCompletion || isPollingAssessmentStatus) && <FullPageLoader isLoading />}
      {showRRMessagePopup &&
        (tcPlusFlow ? (
          <RRMessagePopup
            title={t(rrPopupInfo.title, { firstName: candidate?.firstName })}
            subtitle1={t(rrPopupInfo.subtitle1)}
            subtitle2={t(rrPopupInfo.subtitle2)}
            subtitle3={t(rrPopupInfo.subtitle3)}
            buttonText={t(rrPopupInfo.buttonText)}
            isRRApplied={isRRApplied}
            onRRMsgBtnClick={handleAdvancedRRBtnClick}
            showRRPopup={showRRPopup}
          />
        ) : (
          <PromptBox
            title={t('partialRRMsg1OnTaskPage')}
            subtitle={t('partialRRMsg2OnTaskPage')}
            buttonText={t('gotIt')}
            handleButtonClick={handlePartialRRMsg}
          />
        ))}
      {TaskListLayoutComponentsMap[layout] &&
        React.createElement(TaskListLayoutComponentsMap[layout], {
          taskListConfiguration,
          showTileBanner,
          isICPEnabled,
          desktopAssessments,
          tasks,
          handleAdvancedRRBtnClick,
          tasklistRef,
        })}
    </>
  );
};

export default TaskListPage;
